/**
 * Drill-down filters (ported from the prototype's filterTxns) plus the modal's own refinements.
 */
import type { Txn } from '@/api/types'
import { sumMoney } from '@/lib/format'

export interface DeptTxn extends Txn {
  /** Tile code the row was loaded from. */
  dept: string
}

export type DrillFilter =
  | { type: 'all' }
  | { type: 'acct'; value: string }
  | { type: 'cat'; value: string }
  | { type: 'month'; value: number }
  | { type: 'vendor'; value: string }
  | { type: 'search'; value: string }

export interface DrillRequest {
  /** Tile code, or null for every department (company overview, global search). */
  dept: string | null
  filter: DrillFilter
  label: string
  /** Initial text for the modal's search box (global search). */
  query?: string
}

export function matchesQuery(txn: Txn, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return (
    txn.who.toLowerCase().includes(q) ||
    txn.desc.toLowerCase().includes(q) ||
    txn.title.toLowerCase().includes(q) ||
    txn.acct.includes(q) ||
    txn.doc.toLowerCase().includes(q)
  )
}

export function applyFilter<T extends Txn>(txns: readonly T[], filter: DrillFilter): T[] {
  switch (filter.type) {
    case 'all':
      return [...txns]
    case 'acct':
      return txns.filter((t) => t.acct === filter.value)
    case 'cat':
      return txns.filter((t) => t.cat === filter.value)
    case 'month':
      return txns.filter((t) => t.mo === filter.value)
    case 'vendor':
      return txns.filter((t) => t.vendor === filter.value)
    case 'search':
      return txns.filter((t) => matchesQuery(t, filter.value))
  }
}

export interface Refinement {
  query: string
  /** Calendar month number, or null for every month. */
  month: number | null
}

export function refine<T extends Txn>(txns: readonly T[], refinement: Refinement): T[] {
  return txns.filter(
    (t) => (refinement.month === null || t.mo === refinement.month) && matchesQuery(t, refinement.query),
  )
}

/** Exact total of the rows (summed in cents, so it matches the KPI it drills into). */
export function sumAmounts(txns: readonly Txn[]): number {
  return sumMoney(txns, (t) => t.amt)
}
